import { useCallback, useRef, useState } from 'react';
import type { tLiveAlert } from 'src/alerts/AlertCenter.types';

export type tAlertDetailHost = {
  /** The alert whose detail is open, or null. Mount the modal on this. */
  alert: tLiveAlert | null;
  /** Feed straight into `useAlertCta({ onOpenDetail })` and the row opener. */
  open: (alert: tLiveAlert, invoker: HTMLElement | null) => void;
  /** Pass as the modal's `onClose`. */
  close: () => void;
};

export type tAlertDetailHostOptions = {
  /**
   * Where focus lands when the invoker is gone by close time (the in-page
   * banner decays away while its detail is open — RET-1957 review). Without
   * it, `focus()` on a detached node silently drops focus to `<body>`.
   */
  fallbackFocus?: () => HTMLElement | null;
};

/**
 * Owning an open detail modal, for a surface that STAYS MOUNTED under it
 * (the Avisos tab, the in-page surface).
 *
 * The state is trivial; the focus restore is not, and RET-1956 shipped one
 * copy of it with the restore missing — the invoker travelled all the way
 * from the clicked button into a handler that dropped it, so closing the
 * Avisos tab's detail left focus on `<body>`. Two hand-written copies of a
 * detail two lines long is how that happens again, so it lives here once.
 *
 * `AlertBell` deliberately does NOT use this: its panel closes when the detail
 * opens (RET-1957 U3), so the invoking row is gone by close time and the bell
 * button is the only meaningful destination.
 *
 * The invoker is a REF, not state: it is read once, at close, and storing it
 * in state would re-render the whole surface on every open for a value nothing
 * renders. `isConnected` is the guard that matters — the row that opened the
 * detail can unmount while it is open (the episode resolves underneath), and
 * `focus()` on a detached node silently drops focus to `<body>`.
 */
export function useAlertDetailHost(
  options: tAlertDetailHostOptions = {},
): tAlertDetailHost {
  const [alert, setAlert] = useState<tLiveAlert | null>(null);
  const invokerRef = useRef<HTMLElement | null>(null);
  // Ref-mirrored so `close` keeps a stable identity across renders.
  const fallbackFocusRef = useRef(options.fallbackFocus);
  fallbackFocusRef.current = options.fallbackFocus;

  const open = useCallback((next: tLiveAlert, invoker: HTMLElement | null) => {
    invokerRef.current = invoker;
    setAlert(next);
  }, []);

  const close = useCallback(() => {
    const invoker = invokerRef.current;
    invokerRef.current = null;
    setAlert(null);
    if (invoker?.isConnected) {
      invoker.focus();
      return;
    }
    fallbackFocusRef.current?.()?.focus();
  }, []);

  return { alert, open, close };
}
